import { createClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!url || !key) {
  throw new Error('Missing VITE_SUPABASE_URL or VITE_SUPABASE_PUBLISHABLE_KEY in .env.local');
}

// Wrap fetch with a hard timeout. We've seen Supabase requests hang silently
// in some Chrome profiles — most plausibly a poisoned HTTP/2 connection in
// the per-profile pool (incognito has its own pool and works fine; same
// network on the same machine hangs in the regular profile). AbortController
// kills the stuck request, the browser then opens a fresh connection on the
// next attempt, and meanwhile callers see a real error instead of an
// infinite spinner.
//
// 20s is generous: routine reads finish in <500ms, logo uploads in <2s.
const FETCH_TIMEOUT_MS = 20_000;

function timeoutFetch(input, init = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  // Honour an existing user-supplied signal alongside our timeout.
  const userSignal = init.signal;
  if (userSignal) {
    if (userSignal.aborted) controller.abort();
    else userSignal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  return fetch(input, { ...init, signal: controller.signal })
    .finally(() => clearTimeout(timer));
}

export const supabase = createClient(url, key, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  global: {
    fetch: timeoutFetch,
  },
});
